<template>
  <EditableList
    :items="modelValue"
    add-label="Add rule"
    remove-label="Remove rule"
    @add="addRule"
    @remove="removeRule"
  >
    <template #row="{ item: rule, index }">
      <div class="rule-fields">
        <StField class="field--full" label="Condition">
          <StTextInput
            :model-value="rule.condition"
            placeholder="e.g. Control the objective"
            @update:model-value="updateField(index, 'condition', $event)"
          />
        </StField>
        <StField class="field--narrow" label="Points">
          <StNumberInput
            :model-value="rule.points"
            min="0"
            @update:model-value="updateField(index, 'points', Number($event))"
          />
        </StField>
        <StField class="field--narrow" label="Type">
          <StSelect
            :model-value="rule.controlType"
            @update:model-value="
              updateField(index, 'controlType', $event as 'checkbox' | 'counter')
            "
          >
            <option value="checkbox">Checkbox</option>
            <option value="counter">Counter</option>
          </StSelect>
        </StField>
        <StField v-if="rule.controlType === 'counter'" class="field--narrow" label="Max">
          <StNumberInput
            :model-value="rule.maxCount"
            min="1"
            @update:model-value="updateField(index, 'maxCount', $event)"
          />
        </StField>
        <StField class="field--narrow" label="Resource">
          <StTextInput
            :model-value="rule.resource ?? ''"
            placeholder="optional"
            @update:model-value="updateField(index, 'resource', $event || undefined)"
          />
        </StField>
        <div class="field">
          <span class="st-eyebrow field-label">Rounds (blank = all)</span>
          <div class="rounds-row">
            <StCheckbox
              v-for="r in [1, 2, 3, 4, 5]"
              :key="r"
              :model-value="rule.rounds ? rule.rounds.includes(r) : false"
              @update:model-value="toggleRound(index, r, $event)"
            >
              {{ r }}
            </StCheckbox>
          </div>
        </div>
      </div>
    </template>
  </EditableList>
</template>

<script setup lang="ts">
import type { IScoringRule } from '@scrolldex/shared';
import { StField, StTextInput, StNumberInput, StSelect, StCheckbox } from './ui/index.js';
import EditableList from './EditableList.vue';

const props = defineProps<{
  modelValue: IScoringRule[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: IScoringRule[]): void;
}>();

let _counter = 0;

function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || `rule-${++_counter}`;
}

function addRule() {
  const newRule: IScoringRule = {
    id: `rule-${++_counter}`,
    condition: '',
    points: 1,
    controlType: 'checkbox',
  };
  emit('update:modelValue', [...props.modelValue, newRule]);
}

function removeRule(index: number) {
  const updated = props.modelValue.filter((_, i) => i !== index);
  emit('update:modelValue', updated);
}

function updateField(index: number, field: keyof IScoringRule, value: unknown) {
  const updated = props.modelValue.map((rule, i) => {
    if (i !== index) return rule;
    const next = { ...rule, [field]: value };
    // Regenerate id when condition changes
    if (field === 'condition' && typeof value === 'string') {
      next.id = slugify(value);
    }
    // Remove maxCount when switching away from counter
    if (field === 'controlType' && value !== 'counter') {
      delete next.maxCount;
    }
    return next;
  });
  emit('update:modelValue', updated);
}

function toggleRound(index: number, round: number, checked: boolean) {
  const rule = props.modelValue[index];
  const currentRounds = rule.rounds ? [...rule.rounds] : [];
  let next: number[] | undefined;
  if (checked) {
    next = [...new Set([...currentRounds, round])].sort((a, b) => a - b);
  } else {
    next = currentRounds.filter((r) => r !== round);
  }
  // Empty array means no rounds selected; treat as undefined (all rounds)
  updateField(index, 'rounds', next.length ? next : undefined);
}
</script>

<style scoped>
/* The row card + add/remove chrome lives in EditableList; this owns field layout. */
.rule-fields {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
  align-items: flex-end;
}

.rule-fields :deep(.st-field),
.rule-fields .field {
  flex: 1;
  min-width: 120px;
}

/* Condition takes the whole first row; everything else wraps onto the next line. */
.rule-fields :deep(.field--full) {
  flex: 1 0 100%;
}

.rule-fields :deep(.field--narrow) {
  flex: 0 0 auto;
  min-width: 80px;
  max-width: 120px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-1);
}

.rounds-row {
  display: flex;
  gap: var(--st-space-2);
  align-items: center;
  flex-wrap: wrap;
}
</style>
